/* ==========================================================================
   The motion vocabulary — four durations, one curve, and a list of things
   that must never move.

   THE BAR §7: "an ops console is a place where a moving pixel means the world
   changed. Motion has to be scarce or it stops carrying information."

   MAY MOVE
     hover / focus step            80ms   --dur-hover
     disclosure, tab underline,
     selection, dialog entry      120ms   --dur-1
     a rail or drawer sliding     150ms   --dur-drawer
     an arriving fault            550ms × 2, then it holds  (alarm.ts)
     continuously: the playhead, a live plot's window, video, a 3D viewport,
     ONE 1px indeterminate bar per loading panel, and one 1.5s pulse on a
     LIVE badge that stops the instant you scrub.

   MUST NOT MOVE
     a number that changed        — swap it; a counting odometer makes a stale
                                    value look live
     a chart on entry             — no draw-in, no grow-from-zero, no stagger
     table rows on load or sort   — no fade cascade
     layout on data arrival       — reserve the space (tabular numerals)
     the page between routes      — a console has no page transitions
     anything over 200ms except the playhead

   REDUCED MOTION
     Two switches, and they OR together:
       · the platform's `prefers-reduced-motion: reduce`
       · `data-motion="reduced"` on <html>, which an operator sets from the
         key sheet (a locked-down workstation often cannot change the first)
     Both collapse every transition and animation to 0ms. The playhead, live
     plots and viewport content are rAF-driven — they are information, not
     decoration, and they survive.
   ========================================================================== */

/** ms. The same four numbers the token file publishes as --dur-*. */
export const DUR = {
  hover: 80,
  base: 120,
  drawer: 150,
  /** One half of the two-beat alarm pulse. */
  alarm: 550,
} as const;

export const EASE_OUT = 'cubic-bezier(0.2, 0, 0, 1)';

export type MotionPref = 'system' | 'reduced';

const MEDIA = '(prefers-reduced-motion: reduce)';

/** What the platform itself asks for, ignoring the console's own switch. */
export function platformReducesMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(MEDIA).matches;
}

/** The resolved answer: either switch is enough. */
export function motionIsReduced(pref: MotionPref): boolean {
  return pref === 'reduced' || platformReducesMotion();
}

/** A duration to hand to a JS-driven transition. Zero when motion is reduced,
    so a caller never has to remember to ask. */
export function duration(ms: number, pref: MotionPref): number {
  return motionIsReduced(pref) ? 0 : ms;
}

/** Watch the platform switch. Returns an unsubscribe. */
export function watchPlatformMotion(onChange: (reduced: boolean) => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const mq = window.matchMedia(MEDIA);
  const listener = () => onChange(mq.matches);
  mq.addEventListener('change', listener);
  return () => mq.removeEventListener('change', listener);
}

/** Stamp the console's own switch onto <html>, where global.css reads it. */
export function applyMotionPref(pref: MotionPref): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (pref === 'reduced') root.setAttribute('data-motion', 'reduced');
  else root.removeAttribute('data-motion');
}
